import React, { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Backdrop, Box, Portal, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { selectLoanApplicationControlUI } from 'store/loanApplications/selectors';

import { buildShortcutMap } from '../../builders/navigation.utils';

import { SHORTCUT_PREFIX } from './KeyboardShortcutsPanel.constants';
import {
  shortcutsPanelBackdropStyles,
  shortcutsPanelContainerStyles,
  shortcutsPanelHeaderStyles,
  shortcutsPanelKeyStyles,
  shortcutsPanelLabelStyles,
  shortcutsPanelRowStyles,
  shortcutsPanelSectionHeaderStyles,
  shortcutsPanelSectionStyles,
} from './KeyboardShortcutsPanel.styles';
import { getGeneralShortcuts } from './KeyboardShortcutsPanel.utils';

const KeyboardShortcutsPanel = ({ isOpen, onClose }) => {
  const { availableTabs } = useSelector(selectLoanApplicationControlUI);

  const generalShortcuts = useMemo(() => getGeneralShortcuts(), []);

  const navigationShortcuts = useMemo(() => {
    const { shortcutMap } = buildShortcutMap({ availableTabs });

    return Object.entries(shortcutMap).map(([key, { tabName }]) => ({
      key: `${SHORTCUT_PREFIX}${key}`,
      label: tabName,
    }));
  }, [availableTabs]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <Backdrop open sx={shortcutsPanelBackdropStyles} onClick={onClose} />
      <Box sx={shortcutsPanelContainerStyles}>
        <Typography sx={shortcutsPanelHeaderStyles} variant="medium16">
          Keyboard Shortcuts
        </Typography>

        <Box sx={shortcutsPanelSectionStyles}>
          <Typography sx={shortcutsPanelSectionHeaderStyles} variant="medium12">
            General
          </Typography>
          {generalShortcuts.map(({ key, label }) => (
            <Box key={key} sx={shortcutsPanelRowStyles}>
              <Typography sx={shortcutsPanelKeyStyles} variant="medium12">
                {key}
              </Typography>
              <Typography sx={shortcutsPanelLabelStyles} variant="regular14">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={shortcutsPanelSectionStyles}>
          <Typography sx={shortcutsPanelSectionHeaderStyles} variant="medium12">
            Navigation
          </Typography>
          {navigationShortcuts.map(({ key, label }) => (
            <Box key={key} sx={shortcutsPanelRowStyles}>
              <Typography sx={shortcutsPanelKeyStyles} variant="medium12">
                {key}
              </Typography>
              <Typography sx={shortcutsPanelLabelStyles} variant="regular14">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Portal>
  );
};

KeyboardShortcutsPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(KeyboardShortcutsPanel);
